/**
 * Arrange's second question: who plays.
 *
 * Answered either way round: an ensemble by its name, which seats all its players at once, or
 * one player at a time, and then the other to change what the first gave. Each instrument is
 * a chip with how many of it there are, and its cross takes one away. The lab's roster strip
 * (`ArrangeRoster`) is the same roster with what it will silence said beside it; here nothing
 * is said until there is an arrangement to say it of.
 */

import { ENSEMBLES, type Seat } from "@app/shared-types";
import { oneFewer, oneMore, tally } from "../lib/roster";
import { playerPicks, presetOf, seatLabel } from "./ArrangeRoster";
import ArrangeStep from "./ArrangeStep";
import InstrumentPicker from "./InstrumentPicker";

export interface ArrangeStepPlayersProps {
  readonly roster: readonly string[];
  readonly seats: readonly Seat[];
  /** Why the list of instruments could not be fetched, or `null`. */
  readonly trouble: string | null;
  onRoster(roster: string[]): void;
}

const FIELD = "flex min-w-0 flex-1 basis-[150px] flex-col gap-1 text-[12px] text-shell-text";

export default function ArrangeStepPlayers({
  roster,
  seats,
  trouble,
  onRoster,
}: ArrangeStepPlayersProps): JSX.Element {
  const preset = presetOf(roster);

  return (
    <ArrangeStep number={2} title="Players">
      {roster.length === 0 ? null : (
        <ul className="flex flex-wrap gap-2">
          {[...tally(roster)].map(([instrument, many]) => {
            const label = seatLabel(instrument, seats);
            return (
              <li
                key={instrument}
                className="inline-flex h-11 items-center gap-1 rounded-full bg-shell-key pl-3.5 pr-1 text-[14px] text-shell-lit"
              >
                {label}
                {many === 1 ? null : <span className="font-mono text-[12px]">×{many}</span>}
                <button
                  type="button"
                  onClick={() => onRoster(oneFewer(roster, instrument))}
                  aria-label={`Remove one ${label.toLowerCase()}`}
                  className="h-9 w-9 rounded-full text-[18px] leading-none text-shell-text hover:bg-shell-hot hover:text-shell-lit"
                >
                  ×
                </button>
              </li>
            );
          })}
        </ul>
      )}
      <div className="flex flex-wrap gap-2">
        <label className={FIELD}>
          An ensemble
          <select
            value={preset}
            onChange={(event) => {
              const found = ENSEMBLES.find((one) => one.name === event.target.value);
              if (found !== undefined) onRoster([...found.roster]);
            }}
            className="h-11 rounded-md border border-shell-seam bg-shell-key px-2.5 text-[14px] text-shell-lit outline-none hover:bg-shell-hot focus:border-shell-live"
          >
            {preset === "" ? <option value="">Choose…</option> : null}
            {ENSEMBLES.map((one) => (
              <option key={one.name} value={one.name}>
                {one.name}
              </option>
            ))}
          </select>
        </label>
        <div className={FIELD}>
          <span>or one player at a time</span>
          <InstrumentPicker
            options={playerPicks(seats)}
            value={null}
            placeholder="Add a player…"
            onPick={(instrument) => onRoster(oneMore(roster, instrument))}
            look="form"
            label="Add a player"
          />
        </div>
      </div>
      {trouble === null ? null : (
        <p role="alert" className="rounded-md bg-fault/20 px-3 py-2 text-[13px] text-shell-lit">
          The list of instruments could not be fetched: {trouble}
        </p>
      )}
    </ArrangeStep>
  );
}
